import { useForm, zodResolver } from '@mantine/form'
import { useAddProduct } from 'hooks/products'
import {
  Category,
  FabricVariant,
  NewFabricProduct,
  NewPillowProduct,
  NewPremadeProduct,
  NewProduct,
  NewProductWithoutVariant,
  NewTableProduct,
  NewTowelProduct,
  PillowVariant,
  PremadeVariant,
  TableVariant,
  TowelVariant,
} from 'types/products'
import {
  newFabricProductSchema,
  newPillowProductSchema,
  newPremadeProductSchema,
  newTableProductSchema,
  newTowelProductSchema,
  productSchema,
} from 'types/schemas'

export function useProductForm() {
  const addProduct = useAddProduct({ withoutVariant: true })
  const productForm = useForm<NewProductWithoutVariant>({
    initialValues: {
      symbol: '',
      alternateSymbol: undefined,
      image: undefined,
      isAtSale: false,
      manufacturerId: undefined!,
      availableSizes: [],
    },
    validate: zodResolver(productSchema),
  })
  async function submitProduct(values: NewProductWithoutVariant) {
    await addProduct.mutateAsync(values)
  }

  return { productForm, submitProduct }
}

export function useFabricForm(productId: string | undefined) {
  const addProduct = useAddProduct<FabricVariant>()
  const fabricForm = useForm<NewFabricProduct>({
    initialValues: {
      symbol: '',
      alternateSymbol: undefined!,
      image: undefined,
      isAtSale: false,
      manufacturerId: undefined!,
      availableSizes: [],
      stock: undefined!,
      unit: undefined!,
      place: { rack: undefined!, column: undefined!, shelf: undefined! },
      size: undefined!,
      comments: undefined!,
      feature: undefined!,
    },
    validate: zodResolver(newFabricProductSchema),
  })
  async function submitFabric(values: NewFabricProduct) {
    const submitValues: NewProduct<FabricVariant> = {
      category: Category.FABRIC,
      place: values.place,
      stock: values.stock,
      unit: values.unit,
      size: values.size.toString(),
      comments: values.comments,
      feature: values.feature,
      symbol: values.symbol,
      alternateSymbol: values.alternateSymbol,
      image: values.image,
      isAtSale: values.isAtSale,
      manufacturerId: values.manufacturerId,
      availableSizes: values.availableSizes,
      productId: productId!,
    }
    if (productId) {
      // @ts-ignore
      delete submitValues.symbol
      // @ts-ignore
      delete submitValues.alternateSymbol
      // @ts-ignore
      delete submitValues.image
      // @ts-ignore
      delete submitValues.isAtSale
      // @ts-ignore
      delete submitValues.manufacturerId
      // @ts-ignore
      delete submitValues.availableSizes
    }
    await addProduct.mutateAsync(submitValues)
  }

  return { fabricForm, submitFabric }
}

export function usePremadeForm(productId: string | undefined) {
  const addProduct = useAddProduct<PremadeVariant>()
  const premadeForm = useForm<NewPremadeProduct>({
    initialValues: {
      symbol: '',
      alternateSymbol: undefined!,
      image: undefined,
      isAtSale: false,
      manufacturerId: undefined!,
      availableSizes: [],
      stock: undefined!,
      unit: undefined!,
      place: { rack: undefined!, column: undefined!, shelf: undefined! },
      width: undefined!,
      height: undefined!,
      finish: [],
      comments: undefined!,
    },
    validate: zodResolver(newPremadeProductSchema),
  })
  async function submitPremade(values: NewPremadeProduct) {
    const submitValues: NewProduct<PremadeVariant> = {
      category: Category.PREMADE,
      place: values.place,
      stock: values.stock,
      unit: values.unit,
      size: `${values.width}x${values.height}`,
      comments: values.comments,
      finish: values.finish,
      productId: productId!,
      symbol: values.symbol,
      alternateSymbol: values.alternateSymbol,
      availableSizes: values.availableSizes,
      image: values.image,
      isAtSale: values.isAtSale,
      manufacturerId: values.manufacturerId,
    }
    if (productId) {
      // @ts-ignore
      delete submitValues.symbol
      // @ts-ignore
      delete submitValues.alternateSymbol
      // @ts-ignore
      delete submitValues.image
      // @ts-ignore
      delete submitValues.isAtSale
      // @ts-ignore
      delete submitValues.manufacturerId
      // @ts-ignore
      delete submitValues.availableSizes
    }
    await addProduct.mutateAsync(submitValues)
  }

  return { premadeForm, submitPremade }
}

export function usePillowForm(productId: string | undefined) {
  const addProduct = useAddProduct<PillowVariant>()
  const pillowForm = useForm<NewPillowProduct>({
    initialValues: {
      symbol: '',
      alternateSymbol: undefined,
      image: undefined,
      isAtSale: false,
      manufacturerId: undefined!,
      availableSizes: [],
      stock: undefined!,
      unit: undefined!,
      place: { rack: undefined!, column: undefined!, shelf: undefined! },
      size: undefined!,
      comments: undefined!,
      feature: undefined!,
      pillowType: undefined!,
    },
    validate: zodResolver(newPillowProductSchema),
  })
  async function submitPillow(values: NewPillowProduct) {
    const submitValues: NewProduct<PillowVariant> = {
      category: Category.PILLOW,
      place: values.place,
      stock: values.stock,
      unit: values.unit,
      size: values.size,
      comments: values.comments,
      feature: values.feature,
      pillowType: values.pillowType,
      productId: productId!,
      symbol: values.symbol,
      alternateSymbol: values.alternateSymbol,
      availableSizes: values.availableSizes,
      image: values.image,
      isAtSale: values.isAtSale,
      manufacturerId: values.manufacturerId,
    }
    if (productId) {
      // @ts-ignore
      delete submitValues.symbol
      // @ts-ignore
      delete submitValues.alternateSymbol
      // @ts-ignore
      delete submitValues.image
      // @ts-ignore
      delete submitValues.isAtSale
      // @ts-ignore
      delete submitValues.manufacturerId
      // @ts-ignore
      delete submitValues.availableSizes
    }
    await addProduct.mutateAsync(submitValues)
  }

  return { pillowForm, submitPillow }
}

export function useTowelForm(productId: string | undefined) {
  const addProduct = useAddProduct<TowelVariant>()
  const towelForm = useForm<NewTowelProduct>({
    initialValues: {
      symbol: '',
      alternateSymbol: undefined,
      availableSizes: [],
      image: undefined,
      isAtSale: false,
      manufacturerId: undefined!,
      stock: undefined!,
      unit: undefined!,
      place: { rack: undefined!, column: undefined!, shelf: undefined! },
      width: undefined!,
      length: undefined!,
      comments: undefined!,
      feature: undefined!,
    },
    validate: zodResolver(newTowelProductSchema),
  })
  async function submitTowel(values: NewTowelProduct) {
    const submitValues: NewProduct<TowelVariant> = {
      category: Category.TOWEL,
      place: values.place,
      stock: values.stock,
      unit: values.unit,
      size: `${values.width}x${values.length}`,
      comments: values.comments,
      feature: values.feature,
      productId: productId!,
      symbol: values.symbol,
      alternateSymbol: values.alternateSymbol,
      availableSizes: values.availableSizes,
      image: values.image,
      isAtSale: values.isAtSale,
      manufacturerId: values.manufacturerId,
    }
    if (productId) {
      // @ts-ignore
      delete submitValues.symbol
      // @ts-ignore
      delete submitValues.alternateSymbol
      // @ts-ignore
      delete submitValues.image
      // @ts-ignore
      delete submitValues.isAtSale
      // @ts-ignore
      delete submitValues.manufacturerId
      // @ts-ignore
      delete submitValues.availableSizes
    }
    await addProduct.mutateAsync(submitValues)
  }

  return { towelForm, submitTowel }
}

export function useTableForm(productId: string | undefined) {
  const addProduct = useAddProduct<TableVariant>()
  const tableForm = useForm<NewTableProduct>({
    initialValues: {
      symbol: '',
      alternateSymbol: undefined,
      availableSizes: [],
      image: undefined,
      isAtSale: false,
      manufacturerId: undefined!,
      stock: undefined!,
      unit: undefined!,
      place: { rack: undefined!, column: undefined!, shelf: undefined! },
      width: undefined!,
      length: undefined!,
      comments: undefined!,
      feature: undefined!,
      tableType: undefined!,
    },
    validate: zodResolver(newTableProductSchema),
  })
  async function submitTable(values: NewTableProduct) {
    const submitValues: NewProduct<TableVariant> = {
      category: Category.TABLE,
      place: values.place,
      stock: values.stock,
      unit: values.unit,
      size: `${values.width}x${values.length}`,
      comments: values.comments,
      feature: values.feature,
      tableType: values.tableType,
      productId: productId!,
      symbol: values.symbol,
      alternateSymbol: values.alternateSymbol,
      availableSizes: values.availableSizes,
      image: values.image,
      isAtSale: values.isAtSale,
      manufacturerId: values.manufacturerId,
    }
    if (productId) {
      // @ts-ignore
      delete submitValues.symbol
      // @ts-ignore
      delete submitValues.alternateSymbol
      // @ts-ignore
      delete submitValues.image
      // @ts-ignore
      delete submitValues.isAtSale
      // @ts-ignore
      delete submitValues.manufacturerId
      // @ts-ignore
      delete submitValues.availableSizes
    }
    await addProduct.mutateAsync(submitValues)
  }

  return { tableForm, submitTable }
}
